/**
 * Circular flick controller with power meter overlay.
 * Vector = center → drag position. Circle boundary = max impulse.
 * Touch produces ripple feedback. Power arc shows intensity.
 */

import { useCallback, useRef, useState } from "react";
import {
  type ControllerDragState,
  MAX_DRAG_DISTANCE,
  useFlickController,
} from "../hooks/use-flick-controller";
import { computeIntensityColor } from "../utils/canvas-effects";
import { PowerMeter } from "./PowerMeter";

interface FlickControllerProps {
  readonly size: number;
  readonly isEnabled: boolean;
  readonly selectedPieceId: string | null;
  readonly onFlick: (impulseX: number, impulseY: number) => void;
  readonly onDragStateChange: (state: ControllerDragState | null) => void;
}

const CENTER_DOT_RADIUS = 5;

interface Ripple {
  readonly id: number;
  readonly x: number;
  readonly y: number;
}

export function FlickController({
  size,
  isEnabled,
  selectedPieceId,
  onFlick,
  onDragStateChange,
}: FlickControllerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rippleIdRef = useRef(0);
  const [ripples, setRipples] = useState<ReadonlyArray<Ripple>>([]);

  const radius = size / 2;
  const pixelsPerBoardUnit = radius / MAX_DRAG_DISTANCE;

  const handleFlickResult = useCallback(
    (result: { impulseX: number; impulseY: number }) => {
      onFlick(result.impulseX, result.impulseY);
    },
    [onFlick],
  );

  const { dragState, handlePointerDown, handlePointerMove, handlePointerUp } =
    useFlickController(
      pixelsPerBoardUnit,
      handleFlickResult,
      onDragStateChange,
    );

  const getLocalCoords = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return null;
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    },
    [],
  );

  const addRipple = useCallback((x: number, y: number) => {
    const id = ++rippleIdRef.current;
    setRipples((prev) => [...prev, { id, x, y }]);
    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== id));
    }, 500);
  }, []);

  const handleDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const coords = getLocalCoords(e);
      if (!coords) return;

      addRipple(coords.x, coords.y);

      if (!isEnabled || !selectedPieceId) return;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      handlePointerDown(coords.x, coords.y, radius, radius);
    },
    [
      isEnabled,
      selectedPieceId,
      getLocalCoords,
      handlePointerDown,
      addRipple,
      radius,
    ],
  );

  const handleMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const coords = getLocalCoords(e);
      if (!coords) return;
      handlePointerMove(coords.x, coords.y);
    },
    [getLocalCoords, handlePointerMove],
  );

  const handleUp = useCallback(() => {
    handlePointerUp();
  }, [handlePointerUp]);

  const canDrag = isEnabled && selectedPieceId !== null;
  const isDragging = dragState?.isDragging ?? false;

  const clampedPixelDx = isDragging
    ? (dragState?.dx ?? 0) * pixelsPerBoardUnit
    : 0;
  const clampedPixelDy = isDragging
    ? -(dragState?.dy ?? 0) * pixelsPerBoardUnit
    : 0;

  const dragDistance = dragState
    ? Math.sqrt(dragState.dx * dragState.dx + dragState.dy * dragState.dy)
    : 0;
  const intensity = Math.min(dragDistance / MAX_DRAG_DISTANCE, 1.0);

  const lineColor = isDragging
    ? computeIntensityColor(intensity)
    : "transparent";

  const label = !canDrag
    ? "기물 선택"
    : isDragging
      ? "놓아서 발사"
      : "드래그하여 조준";

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: size,
        height: size,
        borderRadius: "50%",
        backgroundColor: "var(--cn-asf-controller-fill)",
        border: "1px dashed var(--cn-asf-controller-border)",
        touchAction: "none",
        userSelect: "none",
        overflow: "hidden",
        opacity: canDrag ? 1 : 0.4,
        transition: "opacity 0.2s",
      }}
      onPointerDown={handleDown}
      onPointerMove={handleMove}
      onPointerUp={handleUp}
      onPointerCancel={handleUp}
      data-testid="flick-controller"
      role="application"
      aria-label={label}
    >
      {/* Power meter arc */}
      <PowerMeter size={size} intensity={intensity} isActive={isDragging} />

      {/* Ripples */}
      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          style={{
            position: "absolute",
            left: ripple.x,
            top: ripple.y,
            width: 0,
            height: 0,
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            backgroundColor: "var(--cn-asf-aim-line)",
            opacity: 0.18,
            animation: "asf-ripple 0.5s ease-out forwards",
            pointerEvents: "none",
          }}
        />
      ))}

      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
        }}
        aria-hidden="true"
      >
        {/* Max-force boundary ring */}
        <circle
          cx={radius}
          cy={radius}
          r={radius - 6}
          fill="none"
          stroke="var(--cn-asf-controller-border)"
          strokeWidth="1"
          strokeDasharray="6 4"
          opacity={0.4}
        />

        {/* Cross-hair guides */}
        <line
          x1={radius}
          y1={12}
          x2={radius}
          y2={size - 12}
          stroke="var(--cn-asf-controller-cross)"
          strokeWidth="1"
        />
        <line
          x1={12}
          y1={radius}
          x2={size - 12}
          y2={radius}
          stroke="var(--cn-asf-controller-cross)"
          strokeWidth="1"
        />

        {/* Center dot */}
        <circle
          cx={radius}
          cy={radius}
          r={CENTER_DOT_RADIUS}
          fill={isDragging ? lineColor : "var(--cn-asf-text-muted)"}
        />

        {/* Drag vector line + endpoint */}
        {isDragging && (
          <>
            <line
              x1={radius}
              y1={radius}
              x2={radius + clampedPixelDx}
              y2={radius + clampedPixelDy}
              stroke={lineColor}
              strokeWidth={2 + intensity * 2}
              strokeLinecap="round"
              opacity={0.7}
            />
            <circle
              cx={radius + clampedPixelDx}
              cy={radius + clampedPixelDy}
              r={4 + intensity * 3}
              fill={lineColor}
              opacity={0.8}
            />
          </>
        )}
      </svg>

      {/* Label */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          fontSize: "12px",
          color: "var(--cn-asf-text-muted)",
          pointerEvents: "none",
          opacity: isDragging ? 0.2 : 0.7,
          transition: "opacity 0.15s",
        }}
      >
        {label}
      </div>
    </div>
  );
}
